/**
 * Multi-Births by Generation — the dashboard detail page for the ninth control.
 *
 * Laid out as the report is written on paper: the generation number heads its
 * own block, and under it one entry per birth — the year once, then the people
 * born in it named together ("Paulette & Paul, Sr."), then the family line.
 * A set whose members sit on different family lines splits into one named row
 * per line, which is what the year column spanning the whole entry is for.
 *
 * Rows come off /dashboard-statistics-with-details, the payload every other
 * dashboard card already reads, filtered to the members of a multiple-birth set
 * by dashboardMultiBirths.js. The card's count calls the same helper on the same
 * response, so the number on the dashboard and the number in this header cannot
 * drift apart.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { getApi } from "../../../Services/apiService";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import DashboardBackButton from "./DashboardBackButton";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "./DashboardDetailSearchBox";
import DashboardLineColorName from "./DashboardLineColorName";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import useDashboardPhoneLayout from "./useDashboardPhoneLayout";
import {
  DashboardDeceasedAngel,
  DashboardInLawAsterisk,
  DashboardMemberNameLabel,
  DashboardNameMarkerGroup,
  DashboardPhotoModal,
  DashboardPhotoName,
  getDashboardMemberNamePhotoUrls,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import {
  getMultipleBirthLegendTags,
  getMultipleBirthSuperscriptText,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  buildGenderMap,
  buildGenerationLevelMap,
  extractMultiBirthMembers,
  getMultiBirthGender,
  getMultiBirthGeneration,
  getMultiBirthOrderNumber,
  getMultiBirthSetKey,
  getMultiBirthYear,
} from "../../../Utils/dashboardMultiBirths";
import {
  getDashboardMemberNameParts,
  isDashboardInLawMember,
  isDashboardUpcomingDeceasedMember,
} from "../../../Utils/dashboardMemberDisplayName";
import { isExplicitDashboardStartOrTopOfLineMember } from "../../../Utils/dashboardStartOfLineMember";
import {
  dashboardSearchMatches,
  getDashboardMemberSearchText,
  normalizeDashboardSearchText,
} from "../../../Utils/dashboardDetailSearch";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";

const SORTS = {
  GENERATION_ASC: "generation-asc",
  GENERATION_DESC: "generation-desc",
  YEAR_ASC: "year-asc",
  YEAR_DESC: "year-desc",
  ALPHA_ASC: "alpha-asc",
  ALPHA_DESC: "alpha-desc",
};

const DEFAULT_SORT = SORTS.GENERATION_ASC;

/** Printed where the payload names no number. */
const MISSING_VALUE = "—";

/** Stands in for one member's gender in "F/M" when it isn't on record. */
const UNKNOWN_GENDER = "?";

const SortDirectionIcon = ({ direction }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ marginLeft: "4px" }}
    aria-hidden="true"
  >
    <path d={direction === "asc" ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
  </svg>
);

const multiBirthNameOptions = {
  unnamedLabel: "",
  strictHeadForInLaw: true,
  appendDescendantSpouse: false,
};

/** Same in-law test the Birthdays and Memorials rows use for their asterisk. */
const isListedInLawMember = (member) =>
  isDashboardInLawMember(member) &&
  !isExplicitDashboardStartOrTopOfLineMember(member);

/** Unknown numbers sort last in both directions rather than leading the list. */
const compareNumbers = (a, b) => {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
};

/**
 * Colour + hex, normalised the way the legend builder normalises them, so a row
 * and the legend entry for the same family line land on the same key.
 */
const familyLineKey = (colorName, hexCode) =>
  `${String(colorName || "")
    .trim()
    .toLowerCase()
    .replace(/\s+line$/i, "")}|${String(hexCode || "").toLowerCase()}`;

/** "Andre", "Toni & Tommy", "Andre, UnNamed Male 1 & UnNamed Male 2". */
const nameSeparator = (index, total) => {
  if (index >= total - 1) return "";
  return index === total - 2 ? " & " : ", ";
};

function MultiBirthsByGeneration() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } =
    useDashboardPhotoModal();
  const isPhone = useDashboardPhoneLayout();
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [searchQuery, setSearchQuery] = useState("");
  // The search box and the sorts under it centre on the rows themselves.
  const listRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [legendMembers, setLegendMembers] = useState([]);
  // The statistics payload names no generation on its rows, so the headings are
  // filled in from /family-tree-user-ids, which does.
  const [generationById, setGenerationById] = useState(() => new Map());
  // Gender, for member rows that don't carry it themselves.
  const [genderById, setGenderById] = useState(() => new Map());
  const [headsMap, setHeadsMap] = useState(() =>
    buildHeadsOfLineIdByColorMap([])
  );
  useEffect(() => {
    const fetchMultiBirths = async () => {
      try {
        setLoading(true);
        const family_tree_id = getFamilyTreeIdFromStorage();

        if (!family_tree_id) {
          setMembers([]);
          setLegendMembers([]);
          return;
        }

        // Generations are a second call, and a page that failed to load them
        // still lists everyone — the headings read "—" rather than the list
        // disappearing, so the two requests are settled independently.
        const [response, generationsResponse] = await Promise.all([
          getApi(
            `/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`
          ),
          getApi("/family-tree-user-ids", {
            params: { family_tree_id },
          }).catch((error) => {
            console.error("Error fetching generation levels:", error);
            return null;
          }),
        ]);

        setGenerationById(
          buildGenerationLevelMap(generationsResponse?.data?.familyMembers)
        );

        if (response?.statusCode === 200 && response?.data) {
          setHeadsMap(
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(response.data),
              extractAllMembersFromStatisticsResponse(response.data)
            )
          );
          setGenderById(
            buildGenderMap(
              response.data,
              generationsResponse?.data?.familyMembers
            )
          );
          setMembers(extractMultiBirthMembers(response.data));
          setLegendMembers(
            extractAllMembersFromStatisticsResponse(response.data)
          );
        } else {
          setMembers([]);
          setLegendMembers([]);
        }
      } catch (error) {
        console.error("Error fetching multi-births by generation:", error);
        setMembers([]);
        setLegendMembers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMultiBirths();
  }, []);

  // One row per member, with everything the list groups, sorts and measures on.
  const rows = useMemo(
    () =>
      members.map((member, index) => {
        const { baseName, descendantSpouseName } = getDashboardMemberNameParts(
          member,
          multiBirthNameOptions
        );

        return {
          id: String(
            member?.id ?? member?.family_member_id ?? `multi-birth-${index}`
          ),
          member,
          generation:
            getMultiBirthGeneration(member) ??
            generationById.get(
              String(member?.id ?? member?.family_member_id ?? "")
            ) ??
            null,
          year: getMultiBirthYear(member),
          gender:
            getMultiBirthGender(member) ??
            genderById.get(
              String(member?.id ?? member?.family_member_id ?? "")
            ) ??
            null,
          baseName,
          descendantSpouseName,
          sortName: `${baseName} ${descendantSpouseName}`.trim(),
          setKey: getMultiBirthSetKey(member, index),
          orderNumber: getMultiBirthOrderNumber(member),
          isInLaw: isListedInLawMember(member),
          isDeceased: isDashboardUpcomingDeceasedMember(member),
          multipleBirthSup: getMultipleBirthSuperscriptText(member),
          colorName: member?.family_line_color || member?.color_name || "",
          hexCode: member?.family_line_hex_code || member?.hex_code || "#000000",
        };
      }),
    [members, generationById, genderById]
  );

  /**
   * The people of one birth, gathered into a single entry: a year, a generation
   * and the members in birth order, then split into one printed row per family
   * line so a set spanning two lines names each of them.
   */
  const sets = useMemo(() => {
    const byKey = new Map();

    rows.forEach((row) => {
      const existing = byKey.get(row.setKey);
      if (existing) {
        existing.members.push(row);
        return;
      }
      byKey.set(row.setKey, { key: row.setKey, members: [row] });
    });

    return [...byKey.values()].map((set) => {
      const members = [...set.members].sort(
        (a, b) =>
          a.orderNumber - b.orderNumber || a.sortName.localeCompare(b.sortName)
      );

      // Consecutive runs, not a regroup: members stay in birth order, and a run
      // ends where the family line changes.
      const lineGroups = [];
      members.forEach((member) => {
        const lineKey = `${member.colorName}|${member.hexCode}`;
        const current = lineGroups[lineGroups.length - 1];
        if (current && current.lineKey === lineKey) {
          current.members.push(member);
          return;
        }
        lineGroups.push({
          lineKey,
          colorName: member.colorName,
          hexCode: member.hexCode,
          members: [member],
        });
      });

      return {
        key: set.key,
        members,
        lineGroups,
        generation: members.find((m) => m.generation !== null)?.generation ?? null,
        year: members.reduce(
          (earliest, m) =>
            m.year === null ? earliest : Math.min(earliest ?? m.year, m.year),
          null
        ),
        sortName: members[0]?.sortName || "",
        multipleBirthSup: members.find((m) => m.multipleBirthSup)?.multipleBirthSup,
      };
    });
  }, [rows]);

  /**
   * The sets the search box leaves on screen. A birth is one entry, so the
   * whole set stays when any one of its members matches — a twin found by name
   * is shown beside their twin, not on their own.
   */
  const visibleSets = useMemo(() => {
    if (!normalizeDashboardSearchText(searchQuery)) return sets;

    return sets.filter((set) =>
      set.members.some((row) =>
        dashboardSearchMatches(
          getDashboardMemberSearchText(row.member, row.sortName),
          searchQuery
        )
      )
    );
  }, [sets, searchQuery]);

  const visibleRowCount = useMemo(
    () => visibleSets.reduce((sum, set) => sum + set.members.length, 0),
    [visibleSets]
  );

  const isSearching = searchQuery.trim().length > 0;

  /** Sets within a generation, ordered by whichever sort button is live. */
  const compareSets = useMemo(() => {
    const byYear = (a, b) =>
      compareNumbers(a.year, b.year) || a.sortName.localeCompare(b.sortName);

    switch (sortBy) {
      case SORTS.YEAR_DESC:
        return (a, b) =>
          compareNumbers(b.year, a.year) || a.sortName.localeCompare(b.sortName);
      case SORTS.ALPHA_ASC:
        return (a, b) => a.sortName.localeCompare(b.sortName) || byYear(a, b);
      case SORTS.ALPHA_DESC:
        return (a, b) => b.sortName.localeCompare(a.sortName) || byYear(a, b);
      default:
        return byYear;
    }
  }, [sortBy]);

  /**
   * The list as it reads: generation blocks, each holding its births.
   * The Generation button turns the blocks around; the other two reorder the
   * births inside them, so the report stays "by generation" under every sort.
   */
  const generationGroups = useMemo(() => {
    const byGeneration = new Map();

    visibleSets.forEach((set) => {
      const key = set.generation === null ? "unknown" : String(set.generation);
      const existing = byGeneration.get(key);
      if (existing) {
        existing.sets.push(set);
        return;
      }
      byGeneration.set(key, { key, generation: set.generation, sets: [set] });
    });

    const groups = [...byGeneration.values()].map((group) => ({
      ...group,
      sets: [...group.sets].sort(compareSets),
    }));

    const direction = sortBy === SORTS.GENERATION_DESC ? -1 : 1;
    return groups.sort(
      (a, b) => direction * compareNumbers(a.generation, b.generation)
    );
  }, [visibleSets, compareSets, sortBy]);

  /**
   * Head of line per colour, from the same builder the footer legend uses on the
   * other dashboard lists — so the name printed beside a colour here is spelled
   * exactly as that key spells it. This page prints it on the rows instead of in
   * the footer ("Josephine • Coral Line"); the phone repeats them in the footer
   * key as well, as on Family Members. Keyed on colour + hex, with a colour-only fallback for rows
   * whose hex is spelled differently from the heads-of-line record's.
   */
  const legendLines = useMemo(
    () => buildDashboardColorLegendLines(legendMembers, headsMap),
    [legendMembers, headsMap]
  );

  const headNameByLine = useMemo(() => {
    const byColorAndHex = new Map();
    const byColor = new Map();

    legendLines.forEach(
      ({ headName, colorName, hexCode }) => {
        if (!headName) return;
        byColorAndHex.set(familyLineKey(colorName, hexCode), headName);
        const colorOnly = familyLineKey(colorName, "");
        if (!byColor.has(colorOnly)) byColor.set(colorOnly, headName);
      }
    );

    return { byColorAndHex, byColor };
  }, [legendLines]);

  const getLineHeadName = (colorName, hexCode) =>
    headNameByLine.byColorAndHex.get(familyLineKey(colorName, hexCode)) ||
    headNameByLine.byColor.get(familyLineKey(colorName, "")) ||
    "";

  /** Two-state toggle: clicking the live sort flips its direction. */
  const toggleSort = (ascValue, descValue) =>
    setSortBy((current) => (current === ascValue ? descValue : ascValue));

  const sortButton = (label, ascValue, descValue, title) => {
    const isActive = sortBy === ascValue || sortBy === descValue;
    return (
      <button
        type="button"
        className={`dashboard-detail-sort-btn ${isActive ? "active" : ""}`}
        onClick={() => toggleSort(ascValue, descValue)}
        title={title}
      >
        {label}
        {isActive ? (
          <SortDirectionIcon direction={sortBy === ascValue ? "asc" : "desc"} />
        ) : null}
      </button>
    );
  };

  /**
   * Twins split across two family lines are printed on separate rows, so the
   * rows alone no longer read as a pair: tapping either name shows both twins,
   * the one tapped first. Everyone else, or a twin whose sibling has no photo,
   * shows just their own.
   */
  const getSetMemberPhotoUrls = (set, member) => {
    const own = getDashboardMemberNamePhotoUrls(member.member);
    if (set.lineGroups.length < 2 || set.members.length !== 2) return own;

    const twin = set.members.find((m) => m !== member);
    const twinImage = getDashboardMemberNamePhotoUrls(twin?.member).imageUrl;
    if (!own.imageUrl || !twinImage) return own;
    return {
      imageUrl: own.imageUrl,
      secondaryImageUrl: twinImage,
      primaryLabel: member.baseName,
      secondaryLabel: twin.baseName,
    };
  };

  const renderName = (set, member, index, total, trailingSup) => (
    <React.Fragment key={member.id}>
      <DashboardNameMarkerGroup>
        <DashboardPhotoName
          {...getSetMemberPhotoUrls(set, member)}
          onViewPhoto={openPhoto}
        >
          <DashboardMemberNameLabel
            baseName={member.baseName}
            descendantSpouseName={member.descendantSpouseName}
            afterBaseName={
              member.isInLaw && member.descendantSpouseName ? (
                <DashboardInLawAsterisk />
              ) : null
            }
          />
        </DashboardPhotoName>
        {member.isInLaw && !member.descendantSpouseName && (
          <DashboardInLawAsterisk />
        )}
        {member.isDeceased && <DashboardDeceasedAngel />}
        {/* One tag per printed row, after the last name in it — the set size is
            a fact about the birth, not about each person in it. */}
        {index === total - 1 && trailingSup ? (
          <sup className="dashboard-multiple-birth-sup">{trailingSup}</sup>
        ) : null}
      </DashboardNameMarkerGroup>
      {nameSeparator(index, total)}
    </React.Fragment>
  );

  /**
   * One birth as grid rows: the year cell carries the year on the first row
   * only, so a set spanning two family lines prints the year once and the
   * columns still line up down the whole page.
   */
  const renderSet = (set, setIndex) =>
    set.lineGroups.map((lineGroup, lineIndex) => {
      const isSetStart = lineIndex === 0;
      // A blank line's worth of air opens above every birth but the first one
      // under a heading, which already has the heading's own gap above it. The
      // class goes on all three cells so the whole grid row drops together.
      const cellClass = `multi-births-cell${
        isSetStart && setIndex > 0 ? " multi-births-set-spaced" : ""
      }`;

      return (
        <React.Fragment key={`${set.key}-${lineGroup.lineKey}`}>
          <div className={`${cellClass} multi-births-set-year`}>
            {isSetStart ? (set.year === null ? MISSING_VALUE : set.year) : ""}
          </div>
          {/* One letter per person on the row, in the order they're named, so
              a name like Toni that could be either reads unambiguously. */}
          <div className={`${cellClass} multi-births-set-gender`}>
            {lineGroup.members
              .map((member) => member.gender || UNKNOWN_GENDER)
              .join("/")}
          </div>
          <div className={`${cellClass} multi-births-set-names`}>
            {lineGroup.members.map((member, index) =>
              renderName(
                set,
                member,
                index,
                lineGroup.members.length,
                set.multipleBirthSup
              )
            )}
          </div>
          <div className={`${cellClass} multi-births-set-line`}>
            <DashboardLineColorName
              colorName={lineGroup.colorName}
              hexCode={lineGroup.hexCode}
              headName={getLineHeadName(lineGroup.colorName, lineGroup.hexCode)}
            />
          </div>
        </React.Fragment>
      );
    });

  return (
    <div className="dashboard-container multi-births-by-generation-page dashboard-detail-page">
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left">
            <DashboardBackButton />
            <h3 className="dashboard-detail-title">
              Multi-Births by Generation
            </h3>
            {/* Two numbers, because a birth is not a person: the list holds
                9 multiple-birth sets made up of 19 family members. */}
            <div className="dashboard-detail-count">
              {`${visibleSets.length} ${
                visibleSets.length === 1 ? "Set" : "Sets"
              }/${visibleRowCount} ${visibleRowCount === 1 ? "FM" : "FMs"}`}
            </div>
            {!loading && rows.length > 0 && (
              <DashboardDetailSearchBox
                value={searchQuery}
                onChange={setSearchQuery}
                centreOnRef={listRef}
              />
            )}
            {!loading && rows.length > 0 && (
              <div className="dashboard-detail-sort-toggle">
                {sortButton(
                  "Generation",
                  SORTS.GENERATION_ASC,
                  SORTS.GENERATION_DESC,
                  "Sort by Generation"
                )}
                {sortButton(
                  "Year",
                  SORTS.YEAR_ASC,
                  SORTS.YEAR_DESC,
                  "Sort by Year"
                )}
                {sortButton(
                  "A - Z",
                  SORTS.ALPHA_ASC,
                  SORTS.ALPHA_DESC,
                  "Sort by Name"
                )}
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="dashboard-detail-empty-state">
            <p>Loading multi-births...</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="dashboard-detail-empty-state">
            <p>No data for this period.</p>
          </div>
        ) : visibleSets.length === 0 ? (
          <div className="dashboard-detail-empty-state">
            {isSearching ? (
              <DashboardDetailSearchNoMatch query={searchQuery} />
            ) : (
              <p>No data for this period.</p>
            )}
          </div>
        ) : (
          <div className="multi-births-by-generation-page dashboard-detail-content-wrapper">
            <div ref={listRef} className="multi-births-list dashboard-detail-list">
              {generationGroups.map((group) => (
                <React.Fragment key={group.key}>
                  {/* Spans the grid, so the blocks below share one set of
                      columns rather than each aligning to itself. */}
                  <h4 className="multi-births-generation-heading dashboard-section-heading">
                    {group.generation === null ? MISSING_VALUE : group.generation}
                  </h4>
                  {group.sets.map(renderSet)}
                </React.Fragment>
              ))}
            </div>

            <div className="multi-births-by-generation-page in-law-footer">
              {/* Markers, then the phone's family-lines key, as on Family
                  Members. */}
              <DashboardMarkerLegend
                multipleBirthTags={getMultipleBirthLegendTags(members)}
              />
              {isPhone && legendLines.length > 0 && (
                <DashboardColorLegend lines={legendLines} />
              )}
            </div>
          </div>
        )}
      </div>

      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
    </div>
  );
}

export default MultiBirthsByGeneration;
